import { ArrowRight, Gear, House, type Icon as PhosphorIcon } from "@phosphor-icons/react";
import { Link, Navigate, useSearchParams } from "react-router";

/**
 * Landing screen at `/`. For the pilot it simply surfaces the two entry
 * routes so the URLs are settled from the start: the household onboarding
 * flow and the VELUX team admin panel. Matches the onboarding design system.
 */

interface RouteRowProps {
  to: string;
  icon: PhosphorIcon;
  title: string;
  description: string;
}

/**
 * The link-row pattern from velux.com: a flat tinted block, the icon sitting
 * bare on the tint rather than in a tile of its own, and the arrow at the far
 * right. No border — the block colour is what separates the row from the page,
 * which is the whole point of the block treatment.
 */
function RouteRow({ to, icon: Icon, title, description }: RouteRowProps) {
  return (
    <Link
      to={to}
      className="bg-daylight-subtle hover:bg-daylight-soft focus-visible:outline-ring group flex items-center gap-4 rounded-sm p-5 transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2"
    >
      <Icon weight="light" className="text-primary size-7 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-primary text-[17px] leading-6 font-medium">{title}</p>
        <p className="text-muted text-[13px] leading-4">{description}</p>
      </div>
      <ArrowRight weight="light" className="text-primary size-5 shrink-0" aria-hidden />
    </Link>
  );
}

export function SplashPage() {
  const [params] = useSearchParams();

  // Kit flyers point their QR at /onboarding?code=NNNNNN, but a flyer is
  // printed and a URL is easy to get wrong. If a code arrives here instead,
  // forward it rather than asking a homeowner holding their own kit to choose
  // between "Set up my home" and "VELUX team".
  //
  // Forwarded whatever its shape, and left unvalidated here on purpose: whether
  // a code is usable is the onboarding page's question, and it is the only
  // screen that can tell someone their link was wrong. Screening malformed
  // codes out at this door would send them to the staff/homeowner fork with no
  // explanation — the worse of the two dead-ends.
  const codeParam = params.get("code");
  if (codeParam !== null) {
    return <Navigate to={`/onboarding?code=${encodeURIComponent(codeParam)}`} replace />;
  }

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
      {/* Flat brand-Yellow hero block, per velux.com's hero panels. Full
          strength rather than the soft tint the greeting uses: this screen has
          no yellow CTA for it to compete with. */}
      <div className="bg-daylight h-[280px] w-full shrink-0" />
      <div className="flex flex-1 flex-col gap-8 px-6 pt-8 pb-10">
        <div className="flex flex-col gap-3">
          <p className="text-muted type-label">VELUX Smart Climate</p>
          <h1 className="text-primary type-title">Where would you like to go?</h1>
        </div>
        <div className="flex flex-col gap-3">
          <RouteRow
            to="/onboarding"
            icon={House}
            title="Set up my home"
            description="Install your Smart Climate kit, step by step"
          />
          <RouteRow
            to="/admin/households"
            icon={Gear}
            title="VELUX team"
            description="Manage households and monitor setup progress"
          />
        </div>
      </div>
    </div>
  );
}
